import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { Display, setDisplay } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { W_100 } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { ERROR_MESSAGE_SERVICE, ErrorMessageService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { ErrorDebugInfos, ErrorWithCode } from "@cervval.js/common/src/errors/errors";
import { getLogConsole } from "@cervval.js/common/src/services/log.service";
import { getProp, setProp } from "@cervval.js/common/src/tools/any.tools";
import { ObjectTools } from "@cervval.js/common/tools";
import { CjsButton } from "../button/button";
import { I18nType, i18n } from "./i18n/i18n";


/**
 * ErrorDisplay is generally used in a Dialog to display the error content
 * {@link showErrorDialog} function in 'dialog.ts' can be used to build a dialog with an ErrorDisplay
 */

export interface ErrorDisplayOptions {
    /** Show Error Message */
    showErrorMessage: boolean;

    /** Show Debug Infos */
    showDebugInfos: boolean;

    /** The actual error */
    error: ErrorWithCode | string;
}

export const defaultErrorDisplayOptions: ErrorDisplayOptions = {
    /** Show Error Message */
    showErrorMessage: true,

    /** Show Debug Infos */
    showDebugInfos: true,

    /** The actual error */
    error: {} as ErrorWithCode
};

@CustomElement({
    name: "cjs-error-display",
    template: require("./errorDisplay.html"),
    style: require("./errorDisplay.scss"),
    i18n,
    elementCssClasses: [W_100]
})
export class CjsErrorDisplay extends WebComponent<I18nType> {

    @Injected(ERROR_MESSAGE_SERVICE)
    protected readonly _errorMessageService!: ErrorMessageService;

    @TypedWebComponentOptions(defaultErrorDisplayOptions)
    public readonly options!: WebComponentOptions<ErrorDisplayOptions>;

    /** 
     * The components ErrorDisplay Section
     */
    @TypedHtmlElements({ ensureLoadedClasses: [CjsButton] })
    public readonly components!: {
        errorDisplayContainer: HTMLDivElement,
        errorDisplayCode: HTMLDivElement,
        errorDisplayMessage: HTMLPreElement,
        errorDisplayDebugInfos: HTMLPreElement,
        errorDisplayDebugInfosContainer: HTMLDivElement,
        errorDisplayCodeLabel: HTMLParagraphElement,
        errorDisplayMessageLabel: HTMLParagraphElement,
        errorDisplayDebugLabel: HTMLParagraphElement,
        copyDebugInfosBtn: CjsButton,
        textHasBeenCopied: HTMLDivElement
    };

    constructor(options?: Partial<ErrorDisplayOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Init component
     */
    public override init(): void {
        if (typeof this.options.error === "string") {
            setDisplay(this.components.errorDisplayDebugInfosContainer, Display.NONE);
            this.displayErrorMessage(this.options.error);
        } else {

            this.components.errorDisplayCodeLabel.innerText = this.i18n.errorDisplayCodeLabel.label;
            this.components.errorDisplayMessageLabel.innerText = this.i18n.errorDisplayMessageLabel.label;
            this.components.errorDisplayDebugLabel.innerText = this.i18n.errorDisplayDebugLabel.label;

            this.components.errorDisplayCode.innerText = String(this.options.error.code);
            if (this.options.error.errorMessage) {
                this.displayErrorMessage(this.options.error.errorMessage);
            } else {
                this._errorMessageService.getErrorMessage(this.options.error).then((errorMessage) => {
                    if (errorMessage) {
                        this.displayErrorMessage(errorMessage);
                    }
                }).catch(getLogConsole().error);
            }
            this.displayDebugInfos(this.options.error.debugInfos);
        }

        // Add copy in clipboard with btn
        this.components.copyDebugInfosBtn.clickEvent.on(_ => {
            const copyToClipboard = (text: string): void => {
                /* Copy the text inside the text field */
                navigator.clipboard.writeText(text);

                // Display text copied
                setDisplay(this.components.textHasBeenCopied, Display.FLEX);

                // Set text to display
                this.components.textHasBeenCopied.innerHTML = this.i18n.textCopied.label + " !";

                setTimeout(() => {
                    // Hide text copied
                    setDisplay(this.components.textHasBeenCopied, Display.NONE);
                }, 2000);// 2s
            };

            if (typeof this.options.error === "string") {
                copyToClipboard(this.options.error);
            } else {
                const stackTrace = this._getDebugInfosStackStrace(this.options.error?.debugInfos);
                if (stackTrace) {
                    copyToClipboard(stackTrace);
                }
            }
        });
    }

    /**
     * Show/Hide Debug Infos
     */
    public displayDebugInfos(infos?: Partial<ErrorDebugInfos>): void {
        if (this.options.showDebugInfos && infos !== undefined) {

            const debugStackTrace = this._getDebugInfosStackStrace(infos);
            if (debugStackTrace) {
                this.components.errorDisplayDebugInfos.innerHTML = debugStackTrace;
            }
            setDisplay(this.components.errorDisplayDebugInfosContainer, Display.BLOCK);
            setDisplay(this.components.errorDisplayDebugLabel, Display.BLOCK);
        } else {
            setDisplay(this.components.errorDisplayDebugInfosContainer, Display.NONE);
            setDisplay(this.components.errorDisplayDebugLabel, Display.NONE);
        }
    }

    /**
     * Show/Hide Error Message
     */
    public displayErrorMessage(message: string): void {
        if (this.options.showErrorMessage && message !== "") {
            this.components.errorDisplayMessage.innerText = message;
            setDisplay(this.components.errorDisplayMessageLabel, Display.BLOCK);
            setDisplay(this.components.errorDisplayMessage, Display.BLOCK);
        } else {
            setDisplay(this.components.errorDisplayMessage, Display.NONE);
            setDisplay(this.components.errorDisplayMessageLabel, Display.NONE);
        }
    }

    /** Get debug infos stack trace */
    private _getDebugInfosStackStrace(debugInfos?: Partial<ErrorDebugInfos>): string | undefined {

        if (debugInfos === undefined) { return undefined; }

        // Firefox Error.stack is not enumerable and not own property (getter in prototype)
        if (debugInfos.error && getProp(Object.getOwnPropertyNames(debugInfos.error), "stack") === undefined && Object.getPrototypeOf(debugInfos.error)["stack"] !== undefined) {
            setProp(debugInfos.error, "stackTrace", debugInfos.error.stack);
        }

        return JSON.stringify(debugInfos, ObjectTools.onlyOwnPropertiesReplacer, 2).replace(/\\n/g, "\n");
    }

}